import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { RefreshCw, ToggleLeft, ToggleRight } from "lucide-react";
import { featuresApi } from "./api";
import type { FeatureFlag } from "./types";
import { LoadingBlock, ErrorBlock } from "../dashboard/shared/StateBlocks";
import { formatDateTime } from "../applications/shared/format";
import { useToast } from "@/app/providers/Toaster";
import { confirmAction } from "@/app/providers/ConfirmDialog";
import { cn } from "@/lib/utils/cn";

/**
 * Estructura jerárquica de la categoría "portada".
 *
 * En BD las flags son PLANAS (no hay parent/child); el árbol se declara acá y en
 * la portada (Home.jsx). Cada "zona" es un interruptor maestro de un capítulo de
 * la portada: OFF oculta la zona completa (encabezado + todas sus sub-secciones).
 * Cada sub-sección da control granular y sólo se muestra si su zona está ON.
 *
 * Si se agregan flags nuevas con category "portada" que no estén acá, igual se
 * renderizan (fallback al final de la sección) — nunca quedan invisibles.
 */
const PORTADA_STRUCTURE: {
  groups: { key: string; children: string[] }[];
  standalone: string[];
} = {
  groups: [
    {
      key: "PORTADA_CARTELERA",
      children: [
        "PORTADA_ESCENA_NUMEROS",
        "PORTADA_CARTELERA_FILTROS",
        "PORTADA_TRENDING_NOW",
      ],
    },
    {
      key: "PORTADA_DESCUBRE",
      children: [
        "PORTADA_ARTISTAS_DESTACADOS",
        "PORTADA_SUENA_MIXCORE",
        "PORTADA_NUEVO_ESCENA",
        "PORTADA_ARTISTAS",
      ],
    },
    {
      key: "PORTADA_DIARIO",
      children: ["PORTADA_LO_QUE_NO_TE_PIERDES", "PORTADA_RECIEN_ESCENA"],
    },
    {
      key: "PORTADA_SERVICIOS",
      children: [
        "PORTADA_PROVEEDORES_RECOMENDADOS",
        "PORTADA_PROVEEDOR_BANNER",
        "PORTADA_QUIENES_POSIBLE",
      ],
    },
    { key: "PORTADA_SUMATE", children: ["PORTADA_CTA_SUMATE"] },
  ],
  standalone: [],
};

export function FeaturesScreen() {
  const [search, setSearch] = useState("");
  const { push } = useToast();
  const qc = useQueryClient();

  const listQuery = useQuery({
    queryKey: ["operations", "features"],
    queryFn: () => featuresApi.list(),
  });

  const toggleMut = useMutation({
    mutationFn: ({ key, enabled }: { key: string; enabled: boolean }) =>
      featuresApi.toggle(key, enabled),
    onSuccess: (res) => {
      const f = res.data;
      push({
        kind: "success",
        message: `${f.name} → ${f.enabled ? "habilitada" : "deshabilitada"}`,
      });
      qc.invalidateQueries({ queryKey: ["operations", "features"] });
    },
    onError: (err: any) => {
      push({ kind: "error", message: err?.response?.data?.error || "Error al cambiar la feature." });
    },
  });

  // Toggle con confirmación, compartido por la vista plana y la de "portada".
  const requestToggle = async (flag: FeatureFlag, newValue: boolean) => {
    const isPortadaZone = PORTADA_STRUCTURE.groups.some((g) => g.key === flag.key);
    const message = isPortadaZone
      ? `${flag.name} (${flag.key}) — ${
          newValue
            ? "muestra la zona completa en la portada (según qué sub-secciones estén activas)."
            : "oculta la zona COMPLETA de la portada: su encabezado y todas sus sub-secciones."
        }`
      : `${flag.name} (${flag.key}) — ${
          newValue
            ? "afecta a todos los usuarios inmediatamente."
            : "los usuarios que la estuvieran usando pueden ver errores hasta refrescar."
        }`;
    const ok = await confirmAction({
      title: newValue ? "Habilitar feature" : "Deshabilitar feature",
      message,
      confirmLabel: newValue ? "Habilitar" : "Deshabilitar",
      tone: newValue ? "success" : "danger",
    });
    if (ok === true) toggleMut.mutate({ key: flag.key, enabled: newValue });
  };

  const grouped = useMemo(() => {
    const all = listQuery.data?.data ?? [];
    const filtered = all.filter((f) => {
      if (!search) return true;
      const q = search.toLowerCase();
      return (
        f.key.toLowerCase().includes(q) ||
        f.name.toLowerCase().includes(q) ||
        (f.description ?? "").toLowerCase().includes(q)
      );
    });
    const map = new Map<string, FeatureFlag[]>();
    for (const f of filtered) {
      const cat = f.category || "system";
      if (!map.has(cat)) map.set(cat, []);
      map.get(cat)!.push(f);
    }
    for (const list of map.values()) {
      list.sort((a, b) => a.name.localeCompare(b.name));
    }
    return Array.from(map.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [listQuery.data, search]);

  return (
    <div className="flex flex-col gap-4 animate-fade-in">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="nx-eyebrow mb-1">Operaciones · Feature Flags</p>
          <h1 className="nx-h1">Feature flags</h1>
          <p className="text-sm text-fg-muted mt-1">
            Activá/desactivá features de la plataforma. Los cambios son
            inmediatos a nivel global. Pensá dos veces antes de tocar las del
            sistema crítico.
          </p>
        </div>
        <button
          onClick={() => listQuery.refetch()}
          disabled={listQuery.isFetching}
          className="nx-btn-secondary"
        >
          <RefreshCw size={14} className={listQuery.isFetching ? "animate-spin" : ""} />
          Actualizar
        </button>
      </header>

      <input
        type="search"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Buscar por key, nombre o descripción…"
        className="nx-input"
      />

      {listQuery.isLoading ? (
        <LoadingBlock label="Cargando feature flags…" />
      ) : listQuery.isError ? (
        <ErrorBlock onRetry={() => listQuery.refetch()} />
      ) : grouped.length === 0 ? (
        <div className="nx-card p-8 text-center text-fg-subtle text-sm">
          No hay feature flags que coincidan con la búsqueda.
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          {grouped.map(([category, flags]) =>
            category === "portada" ? (
              <PortadaCategory
                key={category}
                flags={flags}
                busy={toggleMut.isPending}
                onToggle={requestToggle}
              />
            ) : (
              <section key={category} className="nx-card overflow-hidden">
                <header className="px-4 py-2 border-b border-border bg-bg-surface">
                  <h2 className="text-xs font-semibold uppercase tracking-wider text-fg-subtle">
                    {category}{" "}
                    <span className="text-fg-muted normal-case">
                      ({flags.length} {flags.length === 1 ? "flag" : "flags"})
                    </span>
                  </h2>
                </header>
                <div className="divide-y divide-border-subtle">
                  {flags.map((flag) => (
                    <FlagRow
                      key={flag.id}
                      flag={flag}
                      busy={toggleMut.isPending}
                      onToggle={(newValue) => requestToggle(flag, newValue)}
                    />
                  ))}
                </div>
              </section>
            ),
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Render especial de la categoría "portada": zonas (grupos) con sus
 * sub-secciones indentadas. Cuando una zona está OFF, sus sub-secciones se
 * muestran atenuadas con un aviso (siguen siendo togglables para pre-configurar).
 */
function PortadaCategory({
  flags,
  busy,
  onToggle,
}: {
  flags: FeatureFlag[];
  busy: boolean;
  onToggle: (flag: FeatureFlag, newValue: boolean) => void;
}) {
  const byKey = new Map(flags.map((f) => [f.key, f]));
  const mappedKeys = new Set<string>();
  PORTADA_STRUCTURE.groups.forEach((g) => {
    mappedKeys.add(g.key);
    g.children.forEach((c) => mappedKeys.add(c));
  });
  PORTADA_STRUCTURE.standalone.forEach((k) => mappedKeys.add(k));
  const unmapped = flags.filter((f) => !mappedKeys.has(f.key));

  return (
    <section className="nx-card overflow-hidden">
      <header className="px-4 py-2 border-b border-border bg-bg-surface">
        <h2 className="text-xs font-semibold uppercase tracking-wider text-fg-subtle">
          portada{" "}
          <span className="text-fg-muted normal-case">
            ({flags.length} flags · zonas y sub-secciones de mixcore.club)
          </span>
        </h2>
      </header>
      <div className="divide-y divide-border-subtle">
        {PORTADA_STRUCTURE.groups.map((group) => {
          const groupFlag = byKey.get(group.key);
          if (!groupFlag) return null;
          const children = group.children
            .map((k) => byKey.get(k))
            .filter((f): f is FeatureFlag => Boolean(f));
          const zoneOff = !groupFlag.enabled;
          const activeChildren = children.filter((c) => c.enabled).length;
          return (
            <div key={group.key}>
              <FlagRow
                flag={groupFlag}
                busy={busy}
                onToggle={(newValue) => onToggle(groupFlag, newValue)}
                isGroup
                badge={
                  children.length > 0
                    ? `${activeChildren}/${children.length} sub-secciones ON`
                    : undefined
                }
              />
              {children.map((child) => (
                <FlagRow
                  key={child.id}
                  flag={child}
                  busy={busy}
                  onToggle={(newValue) => onToggle(child, newValue)}
                  nested
                  mutedReason={zoneOff ? "La zona está desactivada — no se muestra aunque esté ON." : undefined}
                />
              ))}
            </div>
          );
        })}
        {PORTADA_STRUCTURE.standalone.map((k) => {
          const f = byKey.get(k);
          return f ? (
            <FlagRow
              key={f.id}
              flag={f}
              busy={busy}
              onToggle={(newValue) => onToggle(f, newValue)}
            />
          ) : null;
        })}
        {unmapped.map((f) => (
          <FlagRow
            key={f.id}
            flag={f}
            busy={busy}
            onToggle={(newValue) => onToggle(f, newValue)}
          />
        ))}
      </div>
    </section>
  );
}

function FlagRow({
  flag,
  busy,
  onToggle,
  isGroup = false,
  nested = false,
  badge,
  mutedReason,
}: {
  flag: FeatureFlag;
  busy: boolean;
  onToggle: (newValue: boolean) => void;
  isGroup?: boolean;
  nested?: boolean;
  badge?: string;
  mutedReason?: string;
}) {
  return (
    <div
      className={cn(
        "flex items-start gap-3 p-4",
        isGroup && "bg-bg-surface",
        nested && "pl-10 bg-bg-subtle/30",
        mutedReason && "opacity-60",
      )}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1 flex-wrap">
          {nested && <span className="text-fg-subtle text-xs">↳</span>}
          {isGroup && (
            <span className="text-2xs font-semibold uppercase tracking-wide text-accent bg-accent-soft px-1.5 py-0.5 rounded">
              Zona
            </span>
          )}
          <span className={cn("text-sm text-fg", isGroup ? "font-semibold" : "font-medium")}>
            {flag.name}
          </span>
          <code className="text-2xs text-fg-subtle bg-bg-subtle px-1.5 py-0.5 rounded">
            {flag.key}
          </code>
          {badge && (
            <span className="text-2xs text-fg-muted bg-bg-subtle px-1.5 py-0.5 rounded">
              {badge}
            </span>
          )}
        </div>
        {flag.description && (
          <p className="text-2xs text-fg-muted">{flag.description}</p>
        )}
        {mutedReason && (
          <p className="text-2xs text-warning mt-1">{mutedReason}</p>
        )}
        <p className="text-[10px] text-fg-subtle mt-1">
          Actualizada {formatDateTime(flag.updatedAt)}
        </p>
      </div>
      <button
        onClick={() => onToggle(!flag.enabled)}
        disabled={busy}
        className={cn(
          "inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-2xs font-semibold transition-colors disabled:opacity-50",
          flag.enabled
            ? "bg-success-soft text-success hover:bg-success/20"
            : "bg-bg-subtle text-fg-muted hover:bg-bg-hover",
        )}
        title={flag.enabled ? "Click para deshabilitar" : "Click para habilitar"}
      >
        {flag.enabled ? <ToggleRight size={14} /> : <ToggleLeft size={14} />}
        {flag.enabled ? "ON" : "OFF"}
      </button>
    </div>
  );
}
